import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { Borrower } from '../../models/borrower-model';
import { ResidencyAddress } from '../../models/residency-address.model';
import { SubjectProperty } from '../../models/subject-property.model';
import { EnumsService } from '../../services/enums.service';
import { UtilsService } from '../../services/utils.service';
import { WizardFlowServiceBase } from '../../services/wizard/wizard-flow-service.base';
import { MortgageApplication } from '../../models/mortgage-app.model';
import { reoMatchesAddress } from '../reo-address-match.util';
import { isActive } from '../../utils/entity-state';

@Component({
    selector: 'borrower-summary',
    templateUrl: 'borrower-summary.component.html',
    standalone: false
})

export class BorrowerSummaryComponent implements OnInit, OnDestroy {

  private _langChangeSub?: Subscription;

  private _borrower!: Borrower;

  address: string = "";
  fullName: string = "";
  email: string = "";
  phoneNumber: string = "";
  maritalStatus: string = "";
  propertyType: string = "";
  propertyValue: number | undefined;
  residencyBasis: string = "";
  private _durationYears: number = 0;
  private _durationMonths: number = 0;
  get residencyDuration(): string {
    return `${this._durationYears} ${this._transloco.translate('wizard.review.years')} ${this._durationMonths} ${this._transloco.translate('wizard.review.months')} `;
  }
  rent: number | undefined;
  monthlyPayment: number | undefined;
  isMainBorrower: boolean = false;

  @Output()
  borrowerInfoClicked: EventEmitter<any> = new EventEmitter<any>();

  private _mortgage: MortgageApplication;

  @Input()
  set mortgage(mortgage: MortgageApplication) {
    if (!mortgage) return;
    this._mortgage = mortgage;
    const subjectProperty = mortgage.subjectProperty;
    if (subjectProperty) {
      this.propertyType = this._enumsService.propertyTypeName(subjectProperty.propertyType);
    }
  }

  @Input()
  purchasePrice!: number;

  @Input()
  set borrower(borrower: Borrower) {
    this._borrower = borrower;
    this.address = this._utilsService.getCurrentAddressStreetName(this._borrower);
    this.fullName = this._borrower.firstName + " " + this._borrower.lastName;
    this.email = this._borrower.primaryEmail;
    this.phoneNumber = this._utilsService.formatPhoneNumber(this._borrower.mobilePhone);
    this.maritalStatus = this._enumsService.maritalStatusName(this._borrower.maritalStatus);
    const residencyAddress: ResidencyAddress = this._utilsService.getCurrentResidencyAddress(this._borrower);
    this.residencyBasis = this._enumsService.residencyBasisName(residencyAddress.residencyBasis);
    this._durationYears = residencyAddress.durationYears;
    this._durationMonths = residencyAddress.durationMonths;
    if (this.residencyBasis === "Rent") {
      this.rent = residencyAddress.rent;
    } else if (this.residencyBasis === "Own") {
      // LLS-140: normalized match so the owned-residence REO (and its monthly payment) is
      // resolved even when formatting differs from the residency address.
      const reo = this._mortgage?.realEstateOwned.find(r => isActive(r) && reoMatchesAddress(r, residencyAddress.address));
      if (reo) {
        this.monthlyPayment = reo.mortgagePayment;
      }
    }
    this.isMainBorrower = borrower.primaryEmail === this._wizardFlowService.userInfo?.email;
  }

  get borrower(): Borrower {
    return this._borrower;
  }

  detailsHidden: boolean = false;

  constructor(private readonly _utilsService: UtilsService,
    private readonly _enumsService: EnumsService,
    private readonly _wizardFlowService: WizardFlowServiceBase,
    private readonly _transloco: TranslocoService) { }

  ngOnInit() {
    // Re-translate cached enum display names when language switches.
    this._langChangeSub = this._transloco.langChanges$.pipe(skip(1)).subscribe(() => {
      if (this._borrower) {
        this.maritalStatus = this._enumsService.maritalStatusName(this._borrower.maritalStatus);
        const residencyAddress = this._utilsService.getCurrentResidencyAddress(this._borrower);
        this.residencyBasis = this._enumsService.residencyBasisName(residencyAddress.residencyBasis);
      }
      if (this._mortgage?.subjectProperty) {
        this.propertyType = this._enumsService.propertyTypeName(this._mortgage.subjectProperty.propertyType);
      }
    });
  }

  ngOnDestroy(): void {
    this._langChangeSub?.unsubscribe();
  }

  onBorrowerInfoClicked = () => {
    this.borrowerInfoClicked.emit();
  }
}
